import {useAuth} from "./../../../contexts/AuthContext"
import React, {useEffect, useState, Fragment} from "react";
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useNavigate, useMatch} from "react-router-dom";
import {getConversations, getConversation, postConversation, postMessage, deleteConversation, postMessageToAsana} from "../../../api/conversations";
import {getTenantsWithClients} from "../../../api/accounts";
import {getAsanaDocuments, refreshAsana} from "../../../api/documents";
import {Input, Select, TextArea} from "../../elements/forms/Inputs";
import {PrimaryButton, SecondaryButton} from "../../elements/Buttons";
import {PlusIcon, ChatBubbleLeftRightIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {classNames} from "../../../utils/misc";
import {formatDateTime} from "../../../utils/formatters";
import ListItemLoader from "../../elements/ListItemLoader";
import {toast} from "react-toastify";
import {DeleteButton} from "../../elements/DeleteButton";
import {baseHandleChange} from "../../elements/forms/Form";
import {validate} from "../../../utils/validation";
import Joi from "joi";
import ReactMarkdown from 'react-markdown';
import {Dialog, Transition} from "@headlessui/react";

// Mock data for requests
const mockRequestsData = [
    {
        id: 1,
        title: "Website Redesign Request",
        client: {name: "Acme Corp", id: 1},
        source: "Slack",
        sourceUrl: "https://acmecorp.slack.com/archives/C123456/p1706265000",
        artifactUrl: "https://app.asana.com/0/1234567890/1234567891",
        last_message_at: "2024-01-26T10:30:00",
        document: {id: 1},
        document_source_url: "https://app.asana.com/0/1234567890/1234567891",
        messages: [
            {
                id: 1,
                role: "user",
                content: "Hi there,\n\nWe're looking to completely redesign our website. Our current site is outdated and we need a modern, responsive design that works well on mobile devices. We'd like to discuss timeline and pricing.\n\nBest regards,\nJohn Smith\nCEO, Acme Corp",
                created_at: "2024-01-26T10:30:00"
            },
            {
                id: 2,
                role: "assistant",
                content: `## Request Summary

The client is requesting a complete website redesign with the following key requirements:
- Modern, responsive design
- Mobile-optimized experience
- Discussion of timeline and pricing

## Relevant Knowledge

Based on our knowledge base, here are the relevant considerations:

**Design Process:**
- Our standard website redesign process takes 8-12 weeks
- We follow a phased approach: Discovery → Design → Development → Testing → Launch
- Mobile-first design is our default approach

**Pricing Structure:**
- Website redesigns typically range from $15,000 - $50,000 depending on complexity
- Factors affecting price: number of pages, custom features, integrations, content migration
- We offer flexible payment plans

**Previous Similar Projects:**
- Successfully completed 3 website redesigns in the past 6 months
- Average client satisfaction score: 4.8/5
- All projects delivered within 2 weeks of estimated timeline

## Suggested Next Steps

1. **Schedule Discovery Call** - Set up a 60-minute call to understand their specific needs, current pain points, and business goals
2. **Conduct Site Audit** - Review their current website to identify technical issues and improvement opportunities
3. **Prepare Proposal** - Create a detailed proposal including:
   - Project scope and deliverables
   - Timeline with milestones
   - Pricing options (tiered packages)
   - Case studies from similar projects
4. **Send Questionnaire** - Share our pre-project questionnaire to gather information about brand guidelines, target audience, and competitors`,
                created_at: "2024-01-26T10:31:00",
                sources: [
                    {
                        metadata: {
                            title: "Website Redesign Process Guide",
                            sourceUrl: "https://docs.company.com/processes/website-redesign"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "Pricing Guidelines - Web Projects",
                            sourceUrl: "https://docs.company.com/pricing/web-projects"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "Recent Website Projects - Case Studies",
                            sourceUrl: "https://docs.company.com/case-studies/websites"
                        },
                        source_type: "knowledge_base"
                    }
                ]
            }
        ]
    },
    {
        id: 2,
        title: "Website Interaction Issues",
        client: {name: "Retail Plus", id: 2},
        source: "Email",
        sourceUrl: "mailto:sarah.martinez@retailplus.com",
        artifactUrl: "https://app.asana.com/0/1234567890/1234567892",
        last_message_at: "2024-01-26T11:00:00",
        document: {id: 2},
        document_source_url: "https://app.asana.com/0/1234567890/1234567892",
        messages: [
            {
                id: 3,
                role: "user",
                content: "Hey, um, quick thing — since yesterday the website’s been acting weird.\n" +
                    "The homepage loads but when I click any of the buttons it either takes forever or just doesn’t do anything.\n" +
                    "We didn’t change anything on our side as far as I know, but I saw someone mention an update in Slack last week?\n" +
                    "It’s not completely down but it’s definitely not usable for visitors.\n" +
                    "Can someone please take a look today if possible?",
                created_at: "2024-01-26T11:00:00"
            },
            {
                id: 4,
                role: "assistant",
                content: `## Summary

The client reports that the homepage loads, but interactive elements (buttons/links) are slow or unresponsive.

- Issue started "since yesterday"
- Site is partially functional but effectively unusable for visitors
- Client recalls a recent internal update but is unsure of details

## Initial Assessment & Triage

**Request type:** Bug / Production issue

**Urgency:** High (site usability impacted)

**Confidence:** High

**Why urgent:**
User interaction is broken on a live marketing site → direct conversion impact.

## Relevant Internal Context Found

📄 **Asana Ticket #4821** – "Update hero animation script"
- Marked complete 4 days ago
- Assigned to: Alex (frontend)

💬 **Slack message** (last Thursday)
> "Deployed updated animation library on Acme homepage — should improve load performance."

📄 **Internal doc** – "Known issues: GSAP animation conflicts"
- Notes that a specific animation version can block click handlers if loaded asynchronously

## Likely Root Cause

**System suggestion:**

The issue is likely related to the recent homepage animation update. A known conflict with the current animation library can block click events if the script fails to load correctly or times out.

**Confidence level:** Medium–High

**Based on:**
- Timing matches deployment
- Similar issue documented internally
- Symptoms match known behavior (UI loads, interactions fail)

## Recommended Next Steps

1. **Roll back the animation script** to the previous stable version
2. **Confirm whether the issue resolves** on the homepage
3. **If confirmed**, redeploy the updated script with synchronous loading or patched version
4. **Notify the client** that the issue has been identified and is being addressed

**Optional reply draft (for client):**

"Thanks for flagging this — we've identified a likely cause related to a recent frontend update and are already working on a fix. We'll keep you posted shortly."`,
                created_at: "2024-01-26T11:01:00",
                sources: [
                    {
                        metadata: {
                            title: "Asana Ticket #4821 - Update hero animation script",
                            sourceUrl: "https://app.asana.com/0/1234567890/4821"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "Slack - #dev-frontend (last Thursday)",
                            sourceUrl: "https://retailplus.slack.com/archives/C123456/p1706179200"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "Known Issues: GSAP Animation Conflicts",
                            sourceUrl: "https://docs.company.com/troubleshooting/gsap-conflicts"
                        },
                        source_type: "knowledge_base"
                    }
                ]
            }
        ]
    },
    {
        id: 3,
        title: "Social Media Graphics Request",
        client: {name: "TechStart Inc", id: 3},
        source: "Asana",
        sourceUrl: "https://app.asana.com/0/1234567890/1234567893",
        artifactUrl: "https://app.asana.com/0/1234567890/1234567893",
        last_message_at: "2024-01-26T06:00:00",
        document: {id: 3},
        document_source_url: "https://app.asana.com/0/1234567890/1234567893",
        messages: [
            {
                id: 5,
                role: "user",
                content: "New Task: Create 10 social media graphics for upcoming product launch\n\nDue Date: February 5, 2024\nPriority: High\n\nDescription: Need eye-catching graphics for Instagram, LinkedIn, and Twitter to promote our new product launch.",
                created_at: "2024-01-26T06:00:00"
            },
            {
                id: 6,
                role: "assistant",
                content: `## Request Summary

Client needs 10 social media graphics for product launch campaign:
- **Platforms:** Instagram, LinkedIn, Twitter
- **Due Date:** February 5, 2024 (10 days from now)
- **Priority:** High
- **Style:** Eye-catching, promotional

## Relevant Knowledge

**Social Media Design Specifications:**
- **Instagram:** 1080x1080px (square), 1080x1350px (portrait)
- **LinkedIn:** 1200x627px (link posts), 1080x1080px (organic posts)
- **Twitter:** 1200x675px (16:9 ratio)

**Design Best Practices:**
- Use brand colors and fonts consistently
- Include clear call-to-action
- Optimize text for mobile viewing (large, readable fonts)
- Follow 80/20 rule: 80% visual, 20% text
- Create variations for A/B testing

**TechStart Inc Brand Guidelines:**
- Primary colors: Blue (#0066CC), White (#FFFFFF)
- Secondary colors: Gray (#6B7280), Green (#10B981)
- Fonts: Inter (headings), Open Sans (body)
- Logo usage: Minimum 40px height, clear space around logo

**Timeline Considerations:**
- Design phase: 3-4 days
- Client review: 2-3 days
- Revisions: 2-3 days
- Total: 7-10 days (tight but achievable)

## Suggested Next Steps

1. **Clarify Requirements** (Day 1)
   - Schedule 30-minute kickoff call with client
   - Gather product launch details (product name, key features, target audience)
   - Confirm messaging and key talking points
   - Request any existing assets (product photos, logos, brand materials)

2. **Create Design Concepts** (Days 2-3)
   - Develop 2-3 design concepts for client review
   - Create templates that can be adapted across platforms
   - Ensure designs are on-brand and platform-optimized

3. **Client Review & Feedback** (Days 4-5)
   - Present concepts with rationale
   - Gather feedback and select preferred direction
   - Document revision requests

4. **Finalize Designs** (Days 6-8)
   - Complete all 10 graphics in approved style
   - Create platform-specific versions
   - Prepare files in required formats (PNG, JPG)

5. **Delivery & Handoff** (Days 9-10)
   - Deliver organized file package
   - Include posting guidelines and optimal times
   - Provide editable source files for future use`,
                created_at: "2024-01-26T06:01:00",
                sources: [
                    {
                        metadata: {
                            title: "Social Media Design Specifications",
                            sourceUrl: "https://docs.company.com/design/social-media-specs"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "TechStart Inc - Brand Guidelines",
                            sourceUrl: "https://docs.company.com/clients/techstart/brand"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "Design Project Timeline Templates",
                            sourceUrl: "https://docs.company.com/processes/design-timelines"
                        },
                        source_type: "knowledge_base"
                    }
                ]
            }
        ]
    },
    {
        id: 4,
        title: "Strategy Call Request",
        client: {name: "Growth Startup", id: 4},
        source: "Slack",
        sourceUrl: "https://growthstartup.slack.com/archives/C789012/p1706253000",
        artifactUrl: "https://app.asana.com/0/1234567890/1234567894",
        last_message_at: "2024-01-26T07:30:00",
        document: {id: 4},
        document_source_url: "https://app.asana.com/0/1234567890/1234567894",
        messages: [
            {
                id: 7,
                role: "user",
                content: "Hi! Would love to schedule a call this week to discuss our Q2 marketing strategy. Do you have any availability on Thursday or Friday afternoon?",
                created_at: "2024-01-26T07:30:00"
            },
            {
                id: 8,
                role: "assistant",
                content: `## Request Summary

Client requesting strategy call for Q2 marketing planning:
- **Timeframe:** This week (Thursday or Friday afternoon preferred)
- **Topic:** Q2 marketing strategy
- **Type:** Planning/consultation call

## Relevant Knowledge

**Growth Startup - Account Context:**
- **Relationship:** Active client since Q3 2023
- **Services:** Marketing strategy, content creation, social media management
- **Key Contact:** David Park (Marketing Director)
- **Previous Quarter Performance:**
  - 45% increase in social media engagement
  - 3 successful campaigns launched
  - Monthly retainer: $5,000

**Q2 Planning Best Practices:**
- Schedule strategy calls 4-6 weeks before quarter start
- Review Q1 performance metrics before planning Q2
- Typical agenda: performance review, goal setting, campaign planning, budget allocation
- Duration: 60-90 minutes recommended

**Available Team Members:**
- Sarah Chen (Account Manager) - Available Thu 2-4pm, Fri 1-3pm
- Mike Rodriguez (Strategy Lead) - Available Thu 3-5pm, Fri 2-5pm
- Recommended: Both attend for comprehensive strategy discussion

## Suggested Next Steps

1. **Confirm Availability & Send Calendar Invite** (Today)
   - Check team calendar for Thursday/Friday afternoon slots
   - Propose 2-3 specific time options
   - Send calendar invite with Zoom link
   - Duration: 90 minutes

2. **Prepare for Strategy Call** (Before meeting)
   - Pull Q1 performance report (analytics, campaign results, ROI)
   - Review Growth Startup's business goals and KPIs
   - Research industry trends relevant to their sector
   - Prepare Q2 campaign ideas and recommendations
   - Create agenda document

3. **Pre-Call Communication** (2 days before)
   - Send meeting agenda
   - Request any specific topics they want to cover
   - Ask for their Q2 business objectives
   - Share Q1 performance summary for review

4. **Post-Call Follow-Up** (Within 24 hours)
   - Send meeting notes and action items
   - Share Q2 strategy proposal document
   - Provide timeline for deliverables
   - Schedule follow-up check-in`,
                created_at: "2024-01-26T07:31:00",
                sources: [
                    {
                        metadata: {
                            title: "Growth Startup - Client Profile",
                            sourceUrl: "https://docs.company.com/clients/growth-startup/profile"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "Strategy Call Best Practices",
                            sourceUrl: "https://docs.company.com/processes/strategy-calls"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "Q1 2024 Performance Report - Growth Startup",
                            sourceUrl: "https://docs.company.com/reports/growth-startup-q1-2024"
                        },
                        source_type: "knowledge_base"
                    }
                ]
            }
        ]
    },
    {
        id: 5,
        title: "Logo File Format Request",
        client: {name: "TechStart Inc", id: 3},
        source: "Slack",
        sourceUrl: "https://techstart.slack.com/archives/C345678/p1706248500",
        artifactUrl: "https://app.asana.com/0/1234567890/1234567895",
        last_message_at: "2024-01-26T09:15:00",
        document: {id: 5},
        document_source_url: "https://app.asana.com/0/1234567890/1234567895",
        messages: [
            {
                id: 9,
                role: "user",
                content: "Hey! Quick question - can you send me the logo in SVG format? I need it for our new business cards. Thanks!",
                created_at: "2024-01-26T09:15:00"
            },
            {
                id: 10,
                role: "assistant",
                content: `## Request Summary

Client needs logo file in SVG format for business card printing:
- **File Format:** SVG (Scalable Vector Graphics)
- **Use Case:** Business cards
- **Priority:** Standard (quick turnaround expected)

## Relevant Knowledge

**Logo File Management:**
- All client logos stored in organized folder structure: /assets/clients/[client-name]/logos/
- Standard formats maintained: SVG, PNG (transparent), PNG (white bg), JPG, PDF
- SVG files are vector format - ideal for print materials (infinite scalability)

**TechStart Inc Logo Assets:**
- Logo package delivered in December 2023
- Includes: full color, monochrome, and white versions
- All formats available in shared Google Drive folder
- Location: Clients/TechStart Inc/Brand Assets/Logos/

**Business Card Specifications:**
- SVG format is optimal for print quality
- Ensure logo has proper color profile (CMYK for print)
- Recommend providing both RGB and CMYK versions
- Include usage guidelines (minimum size, clear space)

**File Delivery Best Practices:**
- Send via secure link (Google Drive, Dropbox)
- Include multiple format options when possible
- Provide quick reference guide for usage
- Typical response time: Within 2 hours for simple file requests

## Suggested Next Steps

1. **Locate & Prepare Files** (Next 15 minutes)
   - Access TechStart Inc logo folder in shared drive
   - Verify SVG file quality and completeness
   - Check if CMYK version exists (important for print)
   - Prepare additional formats as backup (PDF, high-res PNG)

2. **Send Files with Context** (Next 15 minutes)
   - Create shareable link to logo package
   - Include brief usage notes:
     - Minimum logo size for business cards (typically 0.5" width)
     - Color specifications (CMYK values)
     - Clear space requirements
   - Attach logo usage guidelines PDF if available

3. **Offer Additional Support**
   - Ask if they need help with business card design
   - Offer to review business card proof before printing
   - Provide printer recommendations if needed

4. **Update Asset Log**
   - Note file delivery in client communication log
   - Confirm client received and can access files`,
                created_at: "2024-01-26T09:16:00",
                sources: [
                    {
                        metadata: {
                            title: "Logo File Management Guide",
                            sourceUrl: "https://docs.company.com/assets/logo-management"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "TechStart Inc - Brand Assets Folder",
                            sourceUrl: "https://drive.google.com/drive/folders/techstart-brand-assets"
                        },
                        source_type: "knowledge_base"
                    },
                    {
                        metadata: {
                            title: "Print Design Specifications",
                            sourceUrl: "https://docs.company.com/design/print-specs"
                        },
                        source_type: "knowledge_base"
                    }
                ]
            }
        ]
    }
];


export default function RequestList() {

    const {currentUser} = useAuth()
    const navigate = useNavigate()

    // Use mock data instead of API
    const conversations = mockRequestsData
    const conversationsLoading = false

    const [selectedConversationId, setSelectedConversationId] = useState(null)
    const [newMessage, setNewMessage] = useState("")
    const [messageMode, setMessageMode] = useState('RETRIEVAL_ONLY')

    // Get selected conversation from mock data
    const conversationDetail = conversations.find(c => c.id === selectedConversationId)

    function handleSelectConversation(id) {
        setSelectedConversationId(id)
    }

    async function handleSendMessage() {
        if (!newMessage.trim()) return
        // Mock implementation - just show a toast
        toast.info("Message sending is mocked in this demo")
        setNewMessage("")
    }

    function handleArchiveRequest() {
        toast.success("Request archived")
        setSelectedConversationId(null)
    }

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Sidebar */}
            <ConversationSidebar
                conversations={conversations}
                conversationsLoading={conversationsLoading}
                selectedConversationId={selectedConversationId}
                onSelectConversation={handleSelectConversation}
            />

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col">
                {selectedConversationId ? (
                    <>
                        <ConversationDetail
                            conversation={conversationDetail}
                            isLoading={false}
                        />
                        <ConversationInput
                            message={newMessage}
                            setMessage={setNewMessage}
                            onSend={handleSendMessage}
                            disabled={false}
                            mode={messageMode}
                            setMode={setMessageMode}
                        />
                    </>
                ) : (
                    <EmptyState />
                )}
            </div>

            {/* Side Panel - Always visible when request is selected */}
            {selectedConversationId && conversationDetail && (
                <RequestSidePanel
                    request={conversationDetail}
                    onArchive={handleArchiveRequest}
                />
            )}
        </div>
    )
}

// Sidebar Component
function ConversationSidebar({conversations, conversationsLoading, selectedConversationId, onSelectConversation}) {
    return (
        <aside className="w-80 bg-gray-900 text-white flex flex-col border-r border-gray-700">
            <div className="p-4 border-b border-gray-700">
                <h2 className="text-lg font-semibold">Requests</h2>
            </div>

            {/* Conversations List */}
            <nav className="flex-1 overflow-y-auto p-2">
                {conversationsLoading ? (
                    <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="p-3 bg-gray-800 rounded-lg animate-pulse h-16" />
                        ))}
                    </div>
                ) : conversations && conversations.length > 0 ? (
                    <ul className="space-y-1">
                        {conversations.map((conv) => (
                            <ConversationSidebarItem
                                key={conv.id}
                                conversation={conv}
                                isSelected={selectedConversationId === conv.id}
                                onSelect={() => onSelectConversation(conv.id)}
                            />
                        ))}
                    </ul>
                ) : (
                    <div className="text-center text-gray-400 mt-8">
                        <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No requests yet</p>
                    </div>
                )}
            </nav>
        </aside>
    )
}

// Sidebar Item Component
function ConversationSidebarItem({conversation, isSelected, onSelect}) {
    return (
        <li
            className={classNames(
                "group relative p-3 rounded-lg cursor-pointer transition-colors",
                isSelected ? "bg-gray-700" : "hover:bg-gray-800"
            )}
            onClick={onSelect}
        >
            <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm truncate">
                    {conversation.title || "Untitled Request"}
                </h3>
                {conversation.client?.name && (
                    <p className="text-xs text-gray-400 truncate mt-0.5">
                        {conversation.client.name}
                    </p>
                )}
                {conversation.last_message_at && (
                    <p className="text-xs text-gray-500 mt-1">
                        {formatDateTime(conversation.last_message_at)}
                    </p>
                )}
            </div>
        </li>
    )
}

// Conversation Detail Component
function ConversationDetail({conversation, isLoading}) {
    if (isLoading) {
        return (
            <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-4xl mx-auto space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                            <div className="h-20 bg-gray-200 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (!conversation || !conversation.messages) {
        return (
            <div className="flex-1 flex items-center justify-center text-gray-500">
                <p>No messages yet</p>
            </div>
        )
    }

    return (
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="max-w-4xl mx-auto space-y-4">
                {/* Messages */}
                {conversation.messages.map((message) => (
                    <MessageBubble key={message.id} message={message} conversation={conversation} />
                ))}
            </div>
        </div>
    )
}

// Message Bubble Component
function MessageBubble({message, conversation}) {
    const isUser = message.role === 'user'
    const hasSources = !isUser && message.sources && message.sources.length > 0
    const hasDocument = conversation?.document

    return (
        <div className={classNames(
            "flex",
            isUser ? "justify-end" : "justify-start"
        )}>
            <div className={classNames(
                "max-w-3xl rounded-lg px-4 py-3 shadow-sm",
                isUser
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-900"
            )}>
                <div className="flex items-center gap-2 mb-1">
                    <span className={classNames(
                        "text-xs font-medium",
                        isUser ? "text-indigo-100" : "text-gray-500"
                    )}>
                        {isUser ? "Client Request" : "Triage Result"}
                    </span>
                    {message.created_at && (
                        <span className={classNames(
                            "text-xs",
                            isUser ? "text-indigo-200" : "text-gray-400"
                        )}>
                            {formatDateTime(message.created_at)}
                        </span>
                    )}
                </div>
                <div className="text-sm">
                    {isUser ? (
                        <div className="whitespace-pre-wrap">{message.content}</div>
                    ) : (
                        <div className="prose prose-sm max-w-none">
                            <ReactMarkdown
                                components={{
                                    // Customize markdown rendering
                                    p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                                    ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2" {...props} />,
                                    ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2" {...props} />,
                                    li: ({node, ...props}) => <li className="mb-1" {...props} />,
                                    h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-2 mt-3" {...props} />,
                                    h2: ({node, ...props}) => <h2 className="text-base font-bold mb-2 mt-3" {...props} />,
                                    h3: ({node, ...props}) => <h3 className="text-sm font-bold mb-2 mt-2" {...props} />,
                                    code: ({node, inline, ...props}) =>
                                        inline
                                            ? <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs" {...props} />
                                            : <code className="block bg-gray-100 text-gray-800 p-2 rounded text-xs overflow-x-auto mb-2" {...props} />,
                                    strong: ({node, ...props}) => <strong className="font-bold" {...props} />,
                                    em: ({node, ...props}) => <em className="italic" {...props} />,
                                    a: ({node, ...props}) => <a className="text-indigo-600 hover:text-indigo-800 underline" target="_blank" rel="noopener noreferrer" {...props} />,
                                }}
                            >
                                {message.content}
                            </ReactMarkdown>
                        </div>
                    )}
                </div>

                {/* Sources Section and Asana Button - Always show for assistant messages */}
                {!isUser && (
                    <MessageSourcesAndActions
                        sources={message.sources}
                        messageId={message.id}
                        hasDocument={hasDocument}
                    />
                )}
            </div>
        </div>
    )
}

// Message Sources and Actions Component
function MessageSourcesAndActions({sources, messageId, hasDocument}) {
    const [isExpanded, setIsExpanded] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const hasSources = sources && sources.length > 0

    async function handleSendToAsana() {
        setIsSending(true)
        // Mock implementation
        setTimeout(() => {
            toast.success("Reply sent to Asana")
            setIsSending(false)
        }, 1000)
    }

    return (
        <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
                {hasSources && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <span>Sources ({sources.length})</span>
                        <svg
                            className={classNames(
                                "w-4 h-4 transition-transform",
                                isExpanded && "rotate-180"
                            )}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                )}

                {hasDocument && (
                    <button
                        onClick={handleSendToAsana}
                        disabled={isSending}
                        className={classNames(
                            "text-xs font-medium transition-colors",
                            isSending
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-indigo-600 hover:text-indigo-800"
                        )}
                    >
                        {isSending ? "Sending..." : "Send this reply to Asana"}
                    </button>
                )}
            </div>

            {hasSources && isExpanded && (
                <ul className="mt-2 space-y-2">
                    {sources.map((source, index) => (
                        <SourceItem key={index} source={source} index={index} />
                    ))}
                </ul>
            )}
        </div>
    )
}

// Individual Source Item Component
function SourceItem({source, index}) {
    const metadata = source.metadata || {}
    const title = metadata.title || `Source ${index + 1}`
    const sourceType = source.source_type || metadata.source_type

    // Determine the URL to display/link
    let sourceUrl = null
    let sourceUri = null

    if (metadata.sourceUrl) {
        sourceUrl = metadata.sourceUrl
    } else if (metadata['x-amz-bedrock-kb-source-uri']) {
        sourceUri = metadata['x-amz-bedrock-kb-source-uri']
    } else if (source.location?.url) {
        sourceUrl = source.location.url
    }

    // Use light blue background for knowledge_base sources to indicate higher relevance
    const bgColor = sourceType === 'knowledge_base' ? 'bg-blue-50' : 'bg-gray-50'

    return (
        <li className={classNames("text-xs rounded p-2", bgColor)}>
            <div className="font-medium text-gray-900 mb-1">
                {title}
            </div>
            {sourceUrl ? (
                <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 underline break-all"
                >
                    {sourceUrl}
                </a>
            ) : sourceUri ? (
                <div className="text-gray-600 break-all font-mono text-xs">
                    {sourceUri}
                </div>
            ) : null}
            {metadata.source && (
                <div className="text-gray-500 mt-1">
                    Type: {metadata.source}
                </div>
            )}
        </li>
    )
}

// Conversation Input Component
function ConversationInput({message, setMessage, onSend, disabled, mode, setMode}) {
    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey && !disabled) {
            e.preventDefault()
            onSend()
        }
    }

    return (
        <div className="border-t border-gray-200 bg-white p-4">
            <div className="max-w-4xl mx-auto space-y-3">
                {/* Message Input */}
                <div className="flex gap-3">
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={disabled ? "Waiting for assistant..." : "Type your message... (Press Enter to send, Shift+Enter for new line)"}
                        rows={3}
                        disabled={disabled}
                        className={classNames(
                            "flex-1 resize-none rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent",
                            disabled && "bg-gray-100 cursor-not-allowed"
                        )}
                    />
                    <button
                        onClick={onSend}
                        disabled={!message.trim() || disabled}
                        className={classNames(
                            "px-6 py-3 rounded-lg font-medium transition-colors",
                            message.trim() && !disabled
                                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        )}
                    >
                        {disabled ? "Sending..." : "Send"}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Empty State Component
function EmptyState() {
    return (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
                <ChatBubbleLeftRightIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No request selected</h3>
                <p className="text-gray-500">Select a request from the sidebar to view details</p>
            </div>
        </div>
    )
}

// Request Side Panel Component
function RequestSidePanel({request, onArchive}) {
    return (
        <aside className="w-96 bg-white border-l border-gray-200 flex flex-col overflow-y-auto">
            {/* Header */}
            <div className="bg-indigo-700 px-6 py-4">
                <h2 className="text-lg font-semibold text-white">Request Details</h2>
            </div>

            {/* Content */}
            <div className="flex-1 px-6 py-6 space-y-6">
                {/* Links Section - At the top */}
                <div className="space-y-3">
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Source</h3>
                        <a
                            href={request.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800"
                        >
                            <span>{request.source}</span>
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </a>
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Artifact</h3>
                        <a
                            href={request.artifactUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800"
                        >
                            <span>Asana Ticket</span>
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </a>
                    </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Title</h3>
                    <p className="text-base font-semibold text-gray-900">{request.title}</p>
                </div>

                <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Client</h3>
                    <p className="text-sm text-gray-900">{request.client.name}</p>
                </div>

                <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Last Updated</h3>
                    <p className="text-sm text-gray-900">
                        {formatDateTime(request.last_message_at)}
                    </p>
                </div>

                <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Request Content</h3>
                    <div className="rounded-md bg-gray-50 p-4">
                        <p className="whitespace-pre-wrap text-sm text-gray-900">
                            {request.messages[0]?.content}
                        </p>
                    </div>
                </div>
            </div>

            {/* Archive Button at the bottom */}
            <div className="border-t border-gray-200 px-6 py-4">
                <button
                    onClick={onArchive}
                    className="w-full inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                    Archive
                </button>
            </div>
        </aside>
    )
}
